import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import classes from "./ImportTranscation1.module.css";
import Header from "../../Components/Header/Header";
import Sidebar from "../../Components/Sidebar/Sidebar";
import {
    ChevronLeft,
    ChevronRight,
    Upload,
    FileText,
    CheckCircle,
    // XCircle,
    // Clock,
    ChevronDown,
    Info
} from "lucide-react";

const ImportTransactions = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [selectedFile, setSelectedFile] = useState(null);
    const [dataSource, setDataSource] = useState("");
    const [encoding, setEncoding] = useState("UTF-8");
    const [headerRow, setHeaderRow] = useState(false);
    const [skipEmptyRows, setSkipEmptyRows] = useState(false);

    const steps = [
        {
            id: 1,
            label: "Upload File",
            description: "File uploaded",
            completed: currentStep > 1,
            active: currentStep === 1
        },
        {
            id: 2,
            label: "Map Columns",
            description: "Columns mapped",
            completed: currentStep > 2,
            active: currentStep === 2
        },
        {
            id: 3,
            label: "Preview Data",
            description: "Review and validate data",
            completed: currentStep > 3,
            active: currentStep === 3
        },
        {
            id: 4,
            label: "Import Summary",
            description: "Complete the import",
            completed: currentStep > 4,
            active: currentStep === 4
        }
    ];

    const recentImports = [
        {
            fileName: "Access_Bank_Statement_Aug2026.csv",
            dataSource: "Access Bank (Main Account)",
            importedOn: "14 Aug 2026 10:24 AM",
            status: "Completed"
        },
        {
            fileName: "Zenith_Bank_Statement_Aug2026.xlsx",
            dataSource: "Zenith Bank (Operations)",
            importedOn: "13 Aug 2026 04:32 PM",
            status: "Completed"
        },
        {
            fileName: "GTBank_Statement_Aug2026.csv",
            dataSource: "GT Bank (Payroll Account)",
            importedOn: "14 Aug 2026 10:24 AM",
            status: "Completed"
        },
        {
            fileName: "UBA_Statement_Aug2026.xlsx",
            dataSource: "UBA - Vendors Account",
            importedOn: "14 Aug 2026 10:24 AM",
            status: "Failed"
        },
        {
            fileName: "FirstBank_Statement_Aug2026.csv",
            dataSource: "First Bank (Collections)",
            importedOn: "14 Aug 2026 10:24 AM",
            status: "Completed"
        }
    ];

    const dataSources = [
        "Access Bank (Main Account)",
        "Zenith Bank (Operations)",
        "GT Bank (Payroll Account)",
        "UBA - Vendors Account",
        "First Bank (Collections)"
    ];

    const handleFileDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleBackClick = () => {
        navigate(-1); // Go back to previous page
    };

    const handleNextClick = () => {
        navigate("/ImportTransaction2");
    };

    return (
        <div className={classes.page}>
            <Header />
            <Sidebar />

            <main className={classes.main}>
                <div className={classes.container}>
                    {/* Header Section */}
                    <div className={classes.headerSection}>
                        <div>
                            <h1 className={classes.title}>Import Transactions</h1>
                            <p className={classes.subtitle}>Import bank statement of transaction files into the system</p>
                        </div>
                    </div>

                    {/* Step Indicator */}
                    <div className={classes.stepsContainer}>
                        {steps.map((step, index) => (
                            <React.Fragment key={step.id}>
                                <div className={classes.stepWrapper}>
                                    <div className={`${classes.stepCircle} ${step.completed ? classes.stepCompleted : ''} ${step.active ? classes.stepActive : ''}`}>
                                        {step.completed ? (
                                            <CheckCircle size={20} />
                                        ) : (
                                            <span className={classes.stepNumber}>{step.id}</span>
                                        )}
                                    </div>
                                    <div className={classes.stepContent}>
                                        <div className={`${classes.stepLabel} ${step.active ? classes.stepLabelActive : ''} ${step.completed ? classes.stepLabelCompleted : ''}`}>
                                            {step.id}. {step.label}
                                        </div>
                                        <div className={classes.stepDescription}>{step.description}</div>
                                    </div>
                                </div>
                                {index < steps.length - 1 && (
                                    <div className={`${classes.stepLine} ${step.completed ? classes.stepLineCompleted : ''}`}></div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Upload Section */}
                    <div className={classes.uploadSection}>
                        <h2 className={classes.uploadTitle}>1. Upload File</h2>

                        {/* Select Data Source */}
                        <div className={classes.formGroup}>
                            <label className={classes.formLabel}>Select Data Source</label>
                            <div className={classes.selectWrapper}>
                                <select 
                                    className={classes.formSelect}
                                    value={dataSource}
                                    onChange={(e) => setDataSource(e.target.value)}
                                >
                                    <option value="">Select data source</option>
                                    {dataSources.map((source, index) => (
                                        <option key={index} value={source}>{source}</option>
                                    ))}
                                </select>
                                <ChevronDown size={18} className={classes.selectIcon} />
                            </div>
                            <p className={classes.formHint}>Choose the bank account or data source this file belongs to</p>
                        </div>

                        {/* Select File */}
                        <div className={classes.formGroup}>
                            <label className={classes.formLabel}>Select File</label>
                            <div 
                                className={`${classes.dropZone} ${selectedFile ? classes.hasFile : ''}`}
                                onDrop={handleFileDrop}
                                onDragOver={handleDragOver}
                            >
                                {selectedFile ? (
                                    <div className={classes.fileInfo}>
                                        <FileText size={32} className={classes.fileIcon} />
                                        <span className={classes.fileName}>{selectedFile.name}</span>
                                        <span className={classes.fileSize}>
                                            {(selectedFile.size / 1024).toFixed(2)} KB
                                        </span>
                                        <button 
                                            className={classes.removeFile}
                                            onClick={() => setSelectedFile(null)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : (
                                    <div className={classes.dropContent}>
                                        <Upload size={40} className={classes.uploadIcon} />
                                        <p className={classes.dropText}>Drag and drop files here or click to browse</p>
                                        <p className={classes.dropHint}>CSV, Excel (.xlsx, .xls), TXT (Max, 50MB each)</p>
                                        <input 
                                            type="file" 
                                            className={classes.fileInput}
                                            onChange={handleFileChange}
                                            accept=".csv,.xlsx,.xls,.txt"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* File Encoding */}
                        <div className={classes.formGroup}>
                            <label className={classes.formLabel}>File Encoding</label>
                            <div className={classes.selectWrapper}>
                                <select 
                                    className={classes.formSelect}
                                    value={encoding}
                                    onChange={(e) => setEncoding(e.target.value)}
                                >
                                    <option value="UTF-8">UTF-8 (Recommended)</option>
                                    <option value="ISO-8859-1">ISO-8859-1</option>
                                    <option value="Windows-1252">Windows-1252</option>
                                </select>
                                <ChevronDown size={18} className={classes.selectIcon} />
                            </div>
                            <p className={classes.formHint}>Select the character encoding of your file</p>
                        </div>

                        {/* Import Options */}
                        <div className={classes.optionsGroup}>
                            <h3 className={classes.optionsTitle}>Import Options</h3>
                            <div className={classes.checkboxGroup}>
                                <label className={classes.checkboxLabel}>
                                    <input 
                                        type="checkbox" 
                                        checked={headerRow}
                                        onChange={(e) => setHeaderRow(e.target.checked)}
                                    />
                                    <span>First row contains headers</span>
                                </label>
                                <p className={classes.checkboxHint}>The first row in my file contains column headers</p>
                                
                                <label className={classes.checkboxLabel}>
                                    <input 
                                        type="checkbox" 
                                        checked={skipEmptyRows}
                                        onChange={(e) => setSkipEmptyRows(e.target.checked)}
                                    />
                                    <span>Skip empty rows</span>
                                </label>
                                <p className={classes.checkboxHint}>Ignore empty rows in the file</p>
                            </div>
                        </div>
                    </div>

                    {/* Import Guidelines */}
                    <div className={classes.guidelinesSection}>
                        <h3 className={classes.guidelinesTitle}>
                            <Info size={18} className={classes.guidelinesIcon} />
                            Import Guidelines
                        </h3>
                        <ul className={classes.guidelinesList}>
                            <li>Ensure your file includes all required columns</li>
                            <li>Date format should be YYYY-MM-DD or DD/MM/YYYY</li>
                            <li>Amounts should be in numbers only. Negative amounts should be prefixed with -</li>
                            <li>Duplicate reference numbers may be skipped during import</li>
                            <li>If unsure about column mapping, you can review before importing</li>
                        </ul>
                    </div>

                    {/* Recent Imports */}
                    <div className={classes.recentImports}>
                        <h3 className={classes.recentTitle}>Recent Imports</h3>
                        <div className={classes.tableContainer}>
                            <table className={classes.table}>
                                <thead>
                                    <tr>
                                        <th>File Name</th>
                                        <th>Data Source</th>
                                        <th>Imported On</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentImports.map((item, index) => (
                                        <tr key={index}>
                                            <td className={classes.fileNameCell}>
                                                <FileText size={14} className={classes.fileIconSmall} />
                                                {item.fileName}
                                            </td>
                                            <td>{item.dataSource}</td>
                                            <td>{item.importedOn}</td>
                                            <td>
                                                <span className={`${classes.statusBadge} ${item.status === "Completed" ? classes.statusCompleted : classes.statusFailed}`}>
                                                    {item.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className={classes.pagination}>
                            <span className={classes.paginationInfo}>Showing 1 to 5 of 15 imports</span>
                            <div className={classes.paginationControls}>
                                <button className={classes.pageButton} disabled>Previous</button>
                                <button className={`${classes.pageButton} ${classes.activePage}`}>1</button>
                                <button className={classes.pageButton}>2</button>
                                <button className={classes.pageButton}>3</button>
                                <button className={classes.pageButton}>Next</button>
                            </div>
                        </div>
                    </div>

                    {/* Navigation Buttons */}
                    <div className={classes.navigationButtons}>
                        <button 
                            className={classes.backButton}
                            onClick={handleBackClick}
                        >
                            <ChevronLeft size={18} />
                            Back
                        </button>
                        <button 
                            className={classes.nextButton}
                            onClick={handleNextClick}
                        >
                            Next: Map Columns
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default ImportTransactions;